/* eslint-disable no-underscore-dangle */
import React from "react";
import ActionModal from "./View/DuplicateContactEmailModal";
import ModalWrapper from "components/Global/Modal/Wrappers/Black";

import * as R from "ramda";
import { push } from "react-router-redux";
import { channel } from "redux-saga";
import { actions as TabActions } from "ui-kit/Tabs";
import { showModal } from "redux/modules/modal/actions";
import { actions as SnackbarActions } from "ui-kit/Snackbar/model";
import { SEVERITY } from "ui-kit/Alert/constants";
import { actions, getters } from "./model";
import {
  getGroupDataFields,
  getGroupValues,
  getSpeakerContactDataFields,
  getSpeakerContactValues,
  getGroupValuesInValid,
  getSpeakerContactValuesInvalid,
  getBaseUrl,
  getShouldShowEditDraftSubmission
} from "./selectors";
import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeEvery,
  delay
} from "redux-saga/effects";
import {
  getCredentials,
  user as getUser,
  userId as getUserId
} from "redux/modules/user/selectors";
import { debounce, makeFuture } from "utils/General/sagas";

import Api from "Portal/CallForPapersForm/api";
import { registerError } from "redux/modules/errors/actions";
import PortalUsersApi from "redux/modules/portal/users/api";
import SSOApi from "redux/modules/sso/api";
import UserApi from "redux/modules/user/api";
import Helpers from "utils/Global/Helpers";

import { applyRules, getEnableAdditionalContacts } from "./selectors";
import * as STANDARD_MODULE_FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";

import styles from "Portal/CallForPapersForm/View/styles.scss";

import { isEmptyOrNil, getOptimizedUrl } from "utils/General";
import { apiCall } from "App/Data/sagas";
import { STEPS, STEPS_INDEX_PREVIOUS_PAGE } from "./constants";

const goToPage = function*(stepId, historyReplace = false) {
  const baseUrl = yield select(getBaseUrl);
  const url = `${baseUrl}${
    stepId && stepId !== STEPS.WELCOME ? `/step/${stepId}` : ""
  }`;
  if (historyReplace) {
    window.history.replaceState({}, "", url);
  } else {
    yield put(push(url));
  }
};

const getParams = function*() {
  const formData = yield select(getters.formData);
  const formId = yield select(getters.formId);
  const userId = yield select(getUserId);

  return {
    userId,
    eventId: formData.event_id,
    eventUUID: formData.event_uuid,
    formId
  };
};

const loginChannel = channel();

const preloadImage = imageUrl => {
  if (imageUrl) {
    const preloadImageElement = document.createElement("link");
    preloadImageElement.href = imageUrl;
    preloadImageElement.rel = "preload";
    preloadImageElement.as = "image";
    preloadImageElement.fetchPriority = "high";
    preloadImageElement.loading = "eager";
    document.head.appendChild(preloadImageElement);
  }
};

const onChangeStep = function*({ payload: { stepId } }) {
  yield put(actions.setActiveStep(stepId || STEPS.WELCOME));
  document.body.scrollTop = document.documentElement.scrollTop = 0;
};

const loadDraftSubmission = function*(draftSubmissionId) {
  const credentials = yield select(getCredentials);
  const formId = yield select(getters.formId);

  try {
    yield put(actions.setLoading(true));

    // get form data
    const { payload } = yield call(Api.getForm, {
      credentials,
      formId,
      query: {
        submissionId: draftSubmissionId
      }
    });

    // define variables
    yield put(
      actions.seedDraftSubmission({
        sessionId: payload.draft_id,
        submissionId: draftSubmissionId,
        formPayload: payload,
        speakers: payload.speakers,
        contactValues: {},
        groupValues: payload.sessionValues,
        sections: R.propOr([], "sections", payload)
      })
    );

    yield put(actions.setLoading(false));
  } catch (e) {
    yield all([
      put(
        registerError([
          {
            system: e,
            user: "An error occurred getting submission form data"
          }
        ])
      )
    ]);
  }
  return true;
};

const init = function*({
  payload: { formId, draftId, submissionId, postLogin, stepId }
}) {
  const credentials = yield select(getCredentials);
  const userId = yield select(getUserId);
  const user = yield select(getUser);
  const existingDraftId = yield select(getters.sessionId);

  if (draftId && userId) {
    yield put(actions.setSessionId(draftId));
  }

  if (stepId && userId) {
    yield put(actions.setActiveStep(stepId || STEPS.WELCOME));
  }

  try {
    yield put(actions.setLoading(true));

    // get form data
    const { payload } = yield call(Api.getForm, {
      credentials,
      formId,
      query: {
        submissionId,
        draftId: draftId || existingDraftId
      }
    });

    // optimize images
    const logoImageUrl = R.ifElse(isEmptyOrNil, R.always(null), url =>
      getOptimizedUrl({ url, width: 135, height: 135, quality: 70 })
    )(payload?.styles?.logo_image_url || "");
    yield all([
      call(preloadImage, logoImageUrl),
      put(actions.setFormId(payload.id))
    ]);

    // define variables
    const userEmail = user && user.email ? user.email : null;

    // set form data
    yield put(actions.setFormData(payload));

    if (submissionId && !userId) {
      const baseUrl = yield select(getBaseUrl);
      window.location = `/login?returnTo=${baseUrl}${
        submissionId ? `/submissions/${submissionId}` : ""
      }`;
      return false;
    }

    yield put(
      actions.seedFormData({
        sessionId: payload.draft_id,
        draftSubmissionId: payload.draft_submission_id,
        submissionId,
        userEmail,
        formPayload: payload,
        isLoggedIn: Boolean(userId),
        loggedInFirstName: user.fname,
        loggedInLabel:
          user.fname && user.lname.length
            ? `${user.fname} ${user.lname} (${user.email})`
            : user.email,
        speakers: payload.speakers,
        contactValues: {},
        groupValues: payload.sessionValues,
        speakersLimit: R.prop("speakers_limit", payload),
        sections: R.propOr([], "sections", payload)
      })
    );

    yield put(actions.setLoading(false));

    // if submissionId or postLogin, go to session page
    if (submissionId || postLogin) {
      yield goToPage(STEPS.SESSION);
    }

    // get SSO identity providers
    yield put(actions.fetchSingleSignOnIdentityProviders());
  } catch (e) {
    yield all([
      put(
        registerError([
          {
            system: e,
            user: "An error occurred getting submission form data"
          }
        ])
      )
    ]);
  }
  return true;
};

const handleCallback = function*({ payload }) {
  window.location = `/submit/${payload.slug}/${payload.formId}?postLogin=1`;
  return true;
};

const handleLogin = function*({ payload }) {
  if (payload.err) {
    // eslint-disable-next-line no-console
    console.error(
      `[Call for Papers Landing] Error logging in`,
      payload.err.response.body.message
    );
    yield put(actions.setSearching(false));
    yield put(actions.setError(payload.err.response.body.message));
  } else {
    yield put(actions.setSearching(true));
  }
};

const saveValues = function*(isSavingDraft = false) {
  if (isSavingDraft) {
    yield put(actions.setSavingDraft(true));
  }

  const { eventId, eventUUID, formId } = yield call(getParams);
  const sessionId = yield select(getters.sessionId);
  const submissionId = yield select(getters.submissionId);
  const agreedToTerms = yield select(getters.termsAccepted);
  const groupValues = yield select(getGroupValues);
  const speakers = yield select(getSpeakerContactValues);
  const groupFields = yield select(getGroupDataFields);
  const contactFields = yield select(getSpeakerContactDataFields);
  const credentials = yield select(getCredentials);
  const enableAdditionalContacts = yield select(getEnableAdditionalContacts);
  const speaker = speakers[0];
  const formData = yield select(getters.formData);

  R.forEach(f => {
    const fieldRendered = applyRules(f, groupFields, groupValues);
    if (!fieldRendered) {
      delete groupValues[f.id];
    }
  }, groupFields);
  R.forEach(f => {
    R.forEach(sp => {
      const fieldRendered = applyRules(f, contactFields, sp.contactValues);
      if (!fieldRendered) {
        delete sp.contactValues[f.id];
      }
      if (!enableAdditionalContacts) {
        delete sp.additionalContacts;
      }
    }, speakers);
  }, contactFields);

  yield put(actions.setError(""));

  // create session
  try {
    const {
      payload: {
        session_id: createdSessionId,
        submission_id: createdSubmissionId,
        draft_submission_updated_at: draftSubmissionUpdatedAt
      }
    } = yield call(Api.createOrUpdateDraftSubmission, credentials, formId, {
      isSavingDraft,
      sessionId,
      eventUUID,
      eventId,
      agreedToTerms,
      speakers,
      submissionId,
      groupValues: R.pick(groupFields.map(f => f.id))(groupValues),
      contactValues: R.compose(R.pick(contactFields.map(f => f.id)))(
        speaker.contactValues
      )
    });

    if (isSavingDraft) {
      const activeStep = yield select(getters.activeStep);
      yield put(
        actions.refreshDraftSubmission({
          sessionId: createdSessionId,
          submissionId: createdSubmissionId,
          draftSubmissionUpdatedAt
        })
      );

      // force populating the session ID in the url on the current step
      yield goToPage(activeStep, true);

      yield put(actions.setSavingDraft(false));
    } else {
      yield call(Api.convertDraftSubmission, credentials, {
        formId,
        sessionId: createdSessionId,
        userId: credentials.userId
      });

      // go to confirmation page
      yield goToPage(STEPS.CONFIRMATION);

      // after 7 seconds, redirect to the portal
      yield delay(7000);
      window.location = `/portals/${formData.event_slug}/${eventUUID}`;
    }
  } catch (e) {
    if (isSavingDraft) {
      yield put(actions.setSavingDraft(false));
    } else {
      yield put(actions.setSaving(false));
    }
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred saving session"
        }
      ])
    );
  }
};

const saveDraft = function*() {
  yield call(saveValues, true);

  yield put(
    SnackbarActions.show({
      id: "draft-saved",
      severity: SEVERITY.SUCCESS,
      message: `Draft saved`,
      autoHideDuration: 2000
    })
  );
};

const showDeleteDraftModal = function*() {
  const handleContinue = makeFuture();
  yield put(
    showModal({
      content: (
        <ActionModal
          width={650}
          method="delete"
          confirmLabel="Reset"
          cancelLabel="Cancel"
          message={
            <>
              <div>
                <b>Reset Saved Data</b>
              </div>
              <br />
              <div>
                Would you like to discard your saved draft information?
                <br />
                <br />
                This will delete all your previously saved answers and start a
                new submission.
              </div>
            </>
          }
          onConfirm={() => handleContinue.done(true)}
          onCancel={() => handleContinue.done(false)}
        />
      ),
      disableClickToClose: true,
      wrapper: ModalWrapper
    })
  );
  return yield call(handleContinue.onRealized);
};

const onDeleteDraft = function*() {
  const credentials = yield select(getCredentials);
  const formId = yield select(getters.formId);

  const shouldDelete = yield call(showDeleteDraftModal);
  if (!shouldDelete) {
    return;
  }

  try {
    yield put(actions.setLoading(true));
    const existingDraftId = yield select(getters.sessionId);

    // get form data
    yield call(Api.deleteDraftSubmission, credentials, {
      formId,
      sessionId: existingDraftId
    });

    yield put(actions.resetDraftState());

    yield put(
      actions.init({
        formId,
        draftId: null,
        submissionId: null,
        postLogin: false,
        stepId: STEPS.SESSION
      })
    );

    yield goToPage(STEPS.SESSION, true);

    yield put(actions.setLoading(false));

    yield put(
      SnackbarActions.show({
        id: "draft-deleted",
        severity: SEVERITY.SUCCESS,
        message: `You are now working on a new submission`,
        autoHideDuration: 2000
      })
    );
  } catch (e) {
    yield all([
      put(
        registerError([
          {
            system: e,
            user: "An error occurred deleting draft submission"
          }
        ])
      )
    ]);
  }
};

const onCreateAccount = function*() {
  const { eventId, formId, eventUUID } = yield call(getParams);
  const userExists = yield select(getters.userExists);
  const isVirtualUser = yield select(getters.isVirtualUser);
  const portalUserExists = yield select(getters.portalUserExists);
  const formData = yield select(getters.formData);
  const credentials = yield select(getCredentials);
  const loginValues = yield select(getters.loginValues);
  const userFirstName = yield select(getters.userFirstName);
  const userLastName = yield select(getters.userLastName);
  let email = R.pathOr("", ["email", "value"])(loginValues);
  email = email.toLowerCase().trim();
  const password = R.pathOr("", ["password", "value"])(loginValues);

  yield put(actions.setError(null));
  yield put(actions.setSearching(true));

  try {
    if (userExists === false) {
      // handle: user does not exist
      yield call(UserApi.post, credentials, {
        email,
        password,
        firstName: userFirstName,
        lastName: userLastName,
        eventId,
        contactTypeId: formData.contact_record_type_id,
        createContactAsDraft: false
      });
    } else if (
      userExists === true &&
      isVirtualUser === false &&
      portalUserExists === false
    ) {
      // handle: user exists, is not a virtual user, is not a portal user
    } else if (userExists === true && isVirtualUser === true) {
      // handle: user exists, is a virtual user
      yield call(UserApi.convert, credentials, {
        email,
        password
      });
    }

    window.webAuth.login(
      {
        username: email,
        password,
        realm: window.__AUTH0_REALM__,
        redirectUri: `${window.__LENND_APP_URL__}/submit-callback?eventUUID=${eventUUID}&formId=${formId}&slug=${formData.event_slug}`
      },
      err =>
        loginChannel.put({
          type: actions.handleLogin.type,
          payload: {
            err
          }
        })
    );
  } catch (e) {
    yield put(actions.setSearching(false));
    yield put(actions.setError(e.message));
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred creating account"
        }
      ])
    );
  }
};

const onLogin = function*() {
  yield put(actions.setError(null));
  yield put(actions.setSearching(true));

  const { formId, eventUUID } = yield call(getParams);
  const formData = yield select(getters.formData);
  const loginValues = yield select(getters.loginValues);
  let email = R.pathOr("", ["email", "value"])(loginValues);
  email = email.toLowerCase().trim();
  const password = R.pathOr("", ["password", "value"])(loginValues);

  try {
    window.webAuth.login(
      {
        username: email,
        password,
        realm: window.__AUTH0_REALM__,
        redirectUri: `${window.__LENND_APP_URL__}/submit-callback?eventUUID=${eventUUID}&formId=${formId}&slug=${formData.event_slug}`
      },
      err =>
        loginChannel.put({
          type: actions.handleLogin.type,
          payload: {
            err
          }
        })
    );
  } catch (e) {
    yield put(actions.setSearching(false));
    yield put(
      registerError([
        {
          system: e,
          user: "An error occurred while logging in"
        }
      ])
    );
  }
};

const showError = elm => {
  if (elm) {
    const child = elm.children[0];
    if (child) {
      const entireFieldContainer = child.childNodes;
      if (R.length(entireFieldContainer) > 1) {
        const fieldLabel = entireFieldContainer[0];
        const fieldEditorContainer = child.querySelector(
          ".editor-field-container"
        );
        fieldEditorContainer.classList.add(styles.invalidFieldEditor);
        fieldLabel.classList.remove(styles.validFieldLabel);
        fieldLabel.classList.add(styles.invalidFieldLabel);
      }
    }
  }
};

const scrollToError = function*(
  first = { id: "" },
  selectedSpeakerId,
  index = null
) {
  const { id } = first;
  const queryString = `field-${id}-${selectedSpeakerId}`;
  const elm = yield call([document, document.getElementById], queryString);
  if (elm) {
    showError(elm);
    if (index === 0) {
      document.body.scrollTop = document.documentElement.scrollTop =
        elm.offsetTop;
      const hasIframeEditor = elm.querySelector(".tox-edit-area__iframe");
      if (!R.isNil(hasIframeEditor)) {
        // eslint-disable-next-line no-undef
        tinyMCE.activeEditor.focus();
      } else {
        elm.focus();
      }
    }
  }
};

function* processSpeaker(speaker) {
  const fields = R.propOr([], "fields", speaker);
  const selectedSpeakerId = R.propOr(null, "id", speaker);

  const effects = fields.map((field, index) =>
    call(scrollToError, field, selectedSpeakerId, index)
  );

  yield all(effects);
}

function* processInvalidSpeakersContact(invalidSpeakersContact) {
  const [first] = invalidSpeakersContact;
  if (!R.isNil(first)) {
    const { id = null } = first;
    yield put(TabActions.setSelectedTab(id));
    yield delay(100);
  }

  const speakerEffects = invalidSpeakersContact.map(speaker =>
    call(processSpeaker, speaker)
  );

  yield all(speakerEffects);
}

function* processInvalidGroups(invalidGroups = [], selectedSpeakerId) {
  const invalidGroupsEffects = invalidGroups.map((group, index) =>
    call(scrollToError, group, selectedSpeakerId, index)
  );
  yield all(invalidGroupsEffects);
}

const nextPage = function*() {
  const activeStep = yield select(getters.activeStep);

  if (!activeStep || activeStep === STEPS.WELCOME) {
    // go to login page
    yield goToPage(STEPS.AUTH);
    yield put(actions.setWannaCompleteAndIsInvalid(false));
  } else if (activeStep === STEPS.AUTH) {
    // go to session page
    // if there is a draft submission id and not editing existing submission id,
    // redirect to the draft submission page
    const shouldShowEditDraftSubmission = yield select(
      getShouldShowEditDraftSubmission
    );
    if (shouldShowEditDraftSubmission) {
      const draftSubmissionId = yield select(getters.draftSubmissionId);
      yield call(loadDraftSubmission, draftSubmissionId);
    } else {
      yield put(actions.setWannaCompleteAndIsInvalid(false));
    }
    yield goToPage(STEPS.SESSION);
  } else if (activeStep === STEPS.SESSION) {
    // go to participants page
    const selectedSpeakerId = yield select(getters.selectedSpeakerId);
    const invalidGroups = yield select(getGroupValuesInValid);
    if (!R.isNil(invalidGroups) && !R.isEmpty(invalidGroups)) {
      yield put(actions.setWannaCompleteAndIsInvalid(true));
      yield call(processInvalidGroups, invalidGroups, selectedSpeakerId);
    } else {
      yield goToPage(STEPS.PARTICIPANTS);
      yield put(actions.setWannaCompleteAndIsInvalid(false));
    }
  } else if (activeStep === STEPS.PARTICIPANTS) {
    // save values
    yield put(actions.setSaving(true));

    // delay in case user has jumped straight from the input field to the submit button
    yield delay(300);
    const verifyingEmail = yield select(getters.verifyingEmail);
    if (verifyingEmail) {
      yield put(actions.setSaving(false));
      return;
    }

    const invalidSpeakersContact = yield select(getSpeakerContactValuesInvalid);
    if (
      !R.isNil(invalidSpeakersContact) &&
      !R.isEmpty(invalidSpeakersContact)
    ) {
      yield put(actions.setWannaCompleteAndIsInvalid(true));
      yield call(processInvalidSpeakersContact, invalidSpeakersContact);
      yield put(actions.setSaving(false));
    } else {
      yield call(saveValues);
    }
  }
};

const backPage = function*() {
  const activeStep = yield select(getters.activeStep);

  if (STEPS_INDEX_PREVIOUS_PAGE[activeStep]) {
    yield goToPage(STEPS_INDEX_PREVIOUS_PAGE[activeStep]);
  }
};

const goToLoginPage = function*() {
  const formData = yield select(getters.formData);
  const baseUrl = yield select(getBaseUrl);
  window.location = `/portal-login/${formData.event_slug}/${formData.event_uuid}?returnTo=${baseUrl}`;
};

const goToFormPage = function*() {
  const baseUrl = yield select(getBaseUrl);
  yield put(push(baseUrl));
};

const handleLogout = function*() {
  const baseUrl = yield select(getBaseUrl);
  window.location = `/logout?returnTo=${baseUrl}`;
};

const onEmailUpdate = function*() {
  const loginValues = yield select(getters.loginValues);
  const emailValue = loginValues.email;
  const email = emailValue ? emailValue.value.trim().toLowerCase() : null;

  const { eventId } = yield call(getParams);
  const credentials = yield select(getCredentials);

  yield put(
    actions.updateLoginValues({
      id: "password",
      value: {
        type: "password",
        value: null
      }
    })
  );

  if (!email || !Helpers.isValidEmail(email)) {
    yield put(
      actions.setUserData({
        emailValidated: email,
        userExists: false,
        isVirtualUser: false,
        portalUserExists: false,
        userValidated: false,
        isPasswordValid: false,
        shouldEnforceSso: false,
        disableSignup: false,
        customMessage: null,
        error: "Please enter a valid email address"
      })
    );
    return false;
  }

  yield put(actions.setSearching(true));

  const {
    payload: {
      userExists,
      isVirtualUser,
      portalUserExists,
      shouldEnforceSso,
      disableSignup,
      customMessage
    }
  } = yield call(PortalUsersApi.searchUsers, credentials, {
    eventId,
    email
  });

  yield put(actions.setSearching(false));
  yield put(
    actions.setUserData({
      emailValidated: email,
      userExists,
      shouldEnforceSso,
      disableSignup,
      customMessage,
      isVirtualUser,
      portalUserExists,
      userValidated: true,
      isPasswordValid: false,
      error: null
    })
  );

  return true;
};

const showDuplicateContactEmailModal = function*({ email }) {
  const handleContinue = makeFuture();
  yield put(
    showModal({
      content: (
        <ActionModal
          width={650}
          confirmLabel="Add"
          cancelLabel="Cancel"
          message={
            <>
              <div>
                <b>"{email}" is already associated with a contact profile.</b>
              </div>
              <br />
              <div>
                Would you like to add them to your submission? They will receive
                a confirmation email and can log in to update their details.
              </div>
            </>
          }
          onConfirm={() => handleContinue.done(true)}
          onCancel={() => handleContinue.done(false)}
        />
      ),
      disableClickToClose: true,
      wrapper: ModalWrapper
    })
  );
  return yield call(handleContinue.onRealized);
};

const checkIfContactExists = function*(email) {
  const { eventUUID } = yield call(getParams);
  try {
    yield call(apiCall, {
      method: "post",
      url: `/portal/event/${eventUUID}/contact-exists`,
      data: {
        email
      }
    });

    // If the contact exists, it will throw an error
    return false;
  } catch (error) {
    const message = R.path(["body", "message"], error);

    if (message && message.length) {
      return message;
    }
    throw error;
  }
};

const verifyContactEmail = function*({ payload: selectedSpeakerId }) {
  yield put(actions.setVerifyingEmail(true));

  // get value
  const speakers = yield select(getters.speakers);
  const email = R.compose(
    R.path(["contactValues", STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]),
    R.find(R.propEq("id", selectedSpeakerId))
  )(speakers);

  // get cleaned value
  const cleanedEmail =
    !email || !email.value || !email.value.length
      ? null
      : email.value.trim().toLowerCase();

  // detect, flag and bail if we have an invalid formatted email
  if (!cleanedEmail || !Helpers.isValidEmail(cleanedEmail)) {
    yield all([
      put(actions.setSaving(false)),
      put(actions.setVerifyingEmail(false)),
      put(
        actions.updateSpeakerEmailError({
          selectedSpeakerId,
          emailError: "This is not a valid email"
        })
      )
    ]);
    return false;
  }

  // show saving indicator
  yield put(actions.setSearchingSpeakerEmail(true));

  // if unique contacts is enabled, detect if email exists
  let emailError = "";
  const eventEnableUniqueContacts = yield select(
    getters.eventEnableUniqueContacts
  );
  const user = yield select(getUser);
  const userEmail = user?.email ? user.email : "";
  const isUserEmailSameAsSpeakerEmail =
    cleanedEmail === userEmail.toLowerCase().trim();

  try {
    if (eventEnableUniqueContacts && !isUserEmailSameAsSpeakerEmail) {
      const emailExists = yield call(checkIfContactExists, cleanedEmail);

      if (emailExists) {
        const shouldAdd = yield call(showDuplicateContactEmailModal, {
          email: cleanedEmail
        });

        if (shouldAdd) {
          yield put(
            actions.setSpeakerAsExternal({
              selectedSpeakerId
            })
          );
        } else {
          yield put(
            actions.clearSpeakersEmail({
              selectedSpeakerId
            })
          );
        }
      }
    }
  } catch (error) {
    emailError = R.or(
      R.path(["error", "message"], error),
      R.prop("message", error),
      ""
    );
  }

  // hide saving indicator, show email error
  yield all([
    put(actions.setVerifyingEmail(false)),
    put(actions.setSearchingSpeakerEmail(false)),
    put(actions.setError(emailError)),
    put(
      actions.updateSpeakerEmailError({
        selectedSpeakerId,
        emailError
      })
    )
  ]);

  return true;
};

const onLoginValuesUpdate = function*({ payload: { id, value } }) {
  if (id === "email") {
    const emailValidated = yield select(getters.emailValidated);
    const userValidated = yield select(getters.userValidated);
    const email = value ? value.value.trim().toLowerCase() : null;

    if (userValidated && emailValidated && emailValidated !== email) {
      yield put(
        actions.setUserData({
          emailValidated: null,
          userExists: false,
          isVirtualUser: false,
          portalUserExists: false,
          userValidated: false,
          isPasswordValid: false,
          shouldEnforceSso: false,
          disableSignup: false,
          customMessage: null,
          error: null
        })
      );
    }
  }
};

const onAddSpeaker = function*() {
  const speakers = yield select(getters.speakers);
  const addedSpeakerId = speakers[speakers.length - 1].id;
  yield put(TabActions.setSelectedTab(addedSpeakerId));
  yield put(actions.setSelectedSpeakerId(addedSpeakerId));
  return true;
};

const onTabChange = function*({ payload: tabId }) {
  if (tabId === "add") {
    yield put(actions.addSpeaker());
    yield put(actions.setWannaCompleteAndIsInvalid(false));
    yield call(onAddSpeaker);
  } else {
    yield put(actions.setSelectedSpeakerId(tabId));
  }

  return true;
};

const onRemoveSpeaker = function*() {
  const selectedSpeakerId = yield select(getters.selectedSpeakerId);
  yield put(TabActions.setSelectedTab(selectedSpeakerId));
  return true;
};

const fetchSingleSignOnIdentityProviders = function*() {
  const formId = yield select(getters.formId);
  const { payload } = yield call(SSOApi.formsList, {
    formId
  });
  yield put(actions.setSingleSignOnIdentityProviders(payload));
};

const watchFetchSingleSignOnIdentityProviders = function*() {
  yield takeEvery(
    actions.fetchSingleSignOnIdentityProviders.type,
    fetchSingleSignOnIdentityProviders
  );
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchCallback = function*() {
  yield takeEvery(actions.handleCallback.type, handleCallback);
};

const watchLoginCallback = function*() {
  yield takeEvery(actions.handleLogin.type, handleLogin);
};

const watchLogout = function*() {
  yield takeEvery(actions.handleLogout.type, handleLogout);
};

const watchLoginChannel = function*() {
  while (true) {
    const action = yield take(loginChannel);
    yield put(action);
  }
};

const watchNextPage = function*() {
  yield takeEvery(actions.nextPage.type, nextPage);
};

const watchBackPage = function*() {
  yield takeEvery(actions.backPage.type, backPage);
};

const watchGoToLoginPage = function*() {
  yield takeEvery(actions.goToLoginPage.type, goToLoginPage);
};

const watchGoToFormPage = function*() {
  yield takeEvery(actions.goToFormPage.type, goToFormPage);
};

const watchEmailUpdate = function*() {
  yield takeEvery(
    [actions.validateLogin.type, actions.checkUser.type],
    onEmailUpdate
  );
};

const watchLoginValuesUpdate = function*() {
  yield takeEvery(actions.updateLoginValues.type, onLoginValuesUpdate);
};

const watchChangeTab = function*() {
  yield takeEvery(TabActions.setSelectedTab.type, onTabChange);
};

const watchRemoveSpeaker = function*() {
  yield takeEvery(actions.removeSpeaker.type, onRemoveSpeaker);
};

const watchOnLogin = function*() {
  yield takeEvery(actions.onLogin.type, onLogin);
};

const watchOnCreateAccount = function*() {
  yield takeEvery(actions.onCreateAccount.type, onCreateAccount);
};

const watchClearExternalSpeaker = function*() {
  yield takeEvery(actions.clearExternalSpeaker.type, onRemoveSpeaker);
};

const watchOnChangeStep = function*() {
  yield takeEvery(actions.onChangeStep.type, onChangeStep);
};

const watchSaveDraft = function*() {
  yield takeEvery(actions.saveDraft.type, saveDraft);
};

const watchOnDeleteDraft = function*() {
  yield takeEvery(actions.onDeleteDraft.type, onDeleteDraft);
};

const watchVerifySpeakerEmail = debounce(
  actions.verifySpeakerEmail.type,
  verifyContactEmail,
  200
);

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchCallback),
    fork(watchNextPage),
    fork(watchGoToLoginPage),
    fork(watchGoToFormPage),
    fork(watchEmailUpdate),
    fork(watchLoginValuesUpdate),
    fork(watchLogout),
    fork(watchLoginChannel),
    fork(watchLoginCallback),
    fork(watchBackPage),
    fork(watchChangeTab),
    fork(watchRemoveSpeaker),
    fork(watchOnLogin),
    fork(watchOnCreateAccount),
    fork(watchFetchSingleSignOnIdentityProviders),
    fork(watchVerifySpeakerEmail),
    fork(watchClearExternalSpeaker),
    fork(watchOnChangeStep),
    fork(watchSaveDraft),
    fork(watchOnDeleteDraft)
  ]);
};

export default rootSaga;
